/** Reports are named `<result file name>.data_quality.<pdf|md>`. */
export const QUALITY_REPORT_SUFFIXES = [
  ".data_quality.pdf",
  ".data_quality.md",
] as const;

export type ListedItem = {
  readonly type: string;
  readonly key: string;
};

export type FolderListing = {
  readonly searchQuery: string;
  readonly hasNextPage: boolean;
  readonly pageItems: readonly ListedItem[];
};

export function baseName(key: string): string {
  return key.slice(key.lastIndexOf("/") + 1);
}

function resultOfReport(key: string): string | undefined {
  const lowerKey = key.toLowerCase();
  const suffix = QUALITY_REPORT_SUFFIXES.find((reportSuffix) =>
    lowerKey.endsWith(reportSuffix),
  );
  return suffix ? key.slice(0, key.length - suffix.length) : undefined;
}

export function isQualityReport(key: string): boolean {
  return resultOfReport(key) !== undefined;
}

/** Every key a report of `resultKey` may have; IAM only accepts all-lower or all-upper case. */
export function qualityReportKeys(resultKey: string): string[] {
  return QUALITY_REPORT_SUFFIXES.flatMap((suffix) => [
    `${resultKey}${suffix}`,
    `${resultKey}${suffix.toUpperCase()}`,
  ]);
}

export function keysWithoutPartner(keys: readonly string[]): string[] {
  const available = new Set(keys);
  const reported = new Set(keys.map(resultOfReport));
  return keys.filter((key) => {
    const result = resultOfReport(key);
    return result === undefined ? !reported.has(key) : !available.has(result);
  });
}

export function isCandidateSelection(
  selectedValues: readonly ListedItem[] | undefined,
): boolean {
  return (
    !!selectedValues?.length &&
    selectedValues.every(
      (item) => item.type === "FILE" && !isQualityReport(item.key),
    )
  );
}

export function filesWithoutPartner(dropped: readonly File[]): File[] {
  const unpairedNames = new Set(
    keysWithoutPartner(dropped.map((file) => file.name)),
  );
  return dropped.filter((file) => unpairedNames.has(file.name));
}

/** Empty for a search or a partial page, which could hide a partner. */
export function unpairedListedFiles(listing: FolderListing): string[] {
  if (listing.searchQuery !== "" || listing.hasNextPage) {
    return [];
  }
  const fileKeys = listing.pageItems
    .filter((item) => item.type === "FILE")
    .map((item) => item.key);
  return keysWithoutPartner(fileKeys).map(baseName);
}
